import { Location, isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { variables } from '@shared/helper/configs';
import { SnackbarService } from '../helper/snackbar.service';

@Injectable({ providedIn: 'root' })
export class GeneralService {
  private readonly useragent = signal<string>('');
  ispName = signal<string>('');
  countryCode = signal<string | undefined>(undefined);
  isMac = signal<boolean>(false);
  isIOS = signal<boolean>(false);
  isSafari = signal<boolean>(false);
  isMobile = signal<boolean>(false);
  profileMenus = [
    {
      link: '/account/profiles',
      icon: 'group',
      title: 'مدیریت اعضا',
    },
    {
      link: '/account/devices',
      icon: 'devices',
      title: 'مدیریت دستگاه‌ها',
    },
    {
      link: '/account/plans',
      icon: 'shop',
      title: 'خرید اشتراک',
    },
    {
      link: '/account/tv',
      icon: 'tv',
      title: 'ورود با تلویزیون هوشمند',
    },
    {
      link: '/account/invoices',
      icon: 'group',
      title: 'اطلاعات پرداخت',
    },
    // {
    //   link: '/account/invite',
    //   icon: 'featured_seasonal_and_gifts',
    //   title: 'دعوت از دوستان',
    // },
  ];
  isSmallScreen = computed(() => (this.isBrowserOnly ? window.innerWidth <= 768 : false));

  public router = inject(Router);
  private readonly snackbar = inject(SnackbarService);
  private readonly location = inject(Location);
  private readonly platformId = inject(PLATFORM_ID);

  constructor() {
    if (this.isBrowserOnly) {
      this.useragent.set(navigator.userAgent.toLowerCase());
      this.isIOS.set(!!this.useragent() && /ipad|iphone|ipod/.test(this.useragent()));
      this.isMac.set(!!this.useragent() && /(mac|iphone|ipod|ipad)/i.test(this.useragent()));
      this.isSafari.set(
        !!this.useragent() && /^((?!chrome|android).)*safari/i.test(navigator.userAgent),
      );
      this.isMobile.set(
        !!this.useragent() &&
          /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge|maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i.test(
            navigator.userAgent,
          ),
      );
    }
  }

  get isBrowserOnly(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  get freeIsp(): boolean {
    return [''].includes(this.ispName());
  }

  get freeIspName(): string {
    const dictionary: any = {
      rightel: 'رایتل',
      irancell: 'ایرانسل',
      mci: 'همراه اول',
      aptel: 'آپتل',
    };
    return dictionary[this.ispName()];
  }

  static generateRandomString(len: number): string {
    let text = '';
    const possible =
      'ABCDEF2634GHIJKLM23454g84OPQRg4gSThUVWXYZ4abcdef5345ghijklmn9op9qrstu69wxyz01hp2t34ed56f7b8N9';
    for (let i = 0; i < len; i++) {
      text += possible.charAt(Math.floor(Math.random() * possible.length));
    }
    return text;
  }

  back() {
    this.location.back();
  }

  shuffleArray(array: []): [] {
    let currentIndex = array.length,
      randomIndex;
    while (currentIndex != 0) {
      randomIndex = Math.floor(Math.random() * currentIndex);
      currentIndex--;
      [array[currentIndex], array[randomIndex]] = [array[randomIndex], array[currentIndex]];
    }
    return array;
  }

  deepClean(obj: any) {
    for (var propName in obj) {
      if (obj[propName] === null || obj[propName] === undefined) {
        delete obj[propName];
      }
    }
    return obj;
  }

  shareItem(title: string, summary: string, url?: string) {
    if (this.isBrowserOnly) {
      if (navigator.share) {
        navigator
          .share({
            url: url ?? window.location.href,
            text: summary,
            title: title,
          })
          .then(() => {})
          .catch((error) => {
            window.location.href = url!;
          });
      } else {
        this.copyToClipboard(url ?? window.location.href, 'لینک کپی شد');
      }
    }
  }

  copyToClipboard(text: string, message: string) {
    navigator.clipboard.writeText(text);
    this.snackbar.showSnackbar(message);
  }

  checkShowSubscriptionAlertTooltip(): boolean {
    if (this.isBrowserOnly) {
      const prevTime = sessionStorage.getItem(variables.subscriptionAlertTooltip);
      if (prevTime) {
        const nowTime = new Date().getTime();
        const hour = Math.floor((nowTime - new Date(prevTime!).getTime()) / 3600 / 1000);
        return hour >= 24;
      }
    }
    return true;
  }

  handleDashedColor(remainingDays: number): string[] {
    const green = (isFade = false) => `rgba(91, 226, 143, ${isFade ? '0.32' : '1'})`;
    const limeGreen = (isFade = false) => `rgba(184, 217, 95, ${isFade ? '0.32' : '1'})`;
    const goldenrod = (isFade = false) => `rgba(233, 195, 95, ${isFade ? '0.32' : '1'})`;
    const apricot = (isFade = false) => `rgba(241, 168, 108, ${isFade ? '0.32' : '1'})`;
    const salmonRed = (isFade = false) => `rgba(247, 141, 121, ${isFade ? '0.32' : '1'})`;
    const red = (isFade = false) => `rgba(251, 113, 133, ${isFade ? '0.32' : '1'})`;

    let colors = [red(true), red(true), red(true), red(true), red(true), red(true), red(true)];
    if (remainingDays === 7) {
      colors = [green(), green(), green(), green(), green(), green(), green()];
    } else if (remainingDays === 6) {
      colors = [green(), green(), green(), green(), green(), green(), green(true)];
    } else if (remainingDays === 5) {
      colors = [
        limeGreen(true),
        limeGreen(),
        limeGreen(),
        limeGreen(),
        limeGreen(),
        limeGreen(),
        limeGreen(true),
      ];
    } else if (remainingDays === 4) {
      colors = [
        goldenrod(true),
        goldenrod(true),
        goldenrod(),
        goldenrod(),
        goldenrod(),
        goldenrod(),
        goldenrod(true),
      ];
    } else if (remainingDays === 3) {
      colors = [
        apricot(true),
        apricot(true),
        apricot(true),
        apricot(),
        apricot(),
        apricot(),
        apricot(true),
      ];
    } else if (remainingDays === 2) {
      colors = [
        salmonRed(true),
        salmonRed(true),
        salmonRed(true),
        salmonRed(true),
        salmonRed(),
        salmonRed(),
        salmonRed(true),
      ];
    } else if (remainingDays === 1) {
      colors = [red(true), red(true), red(true), red(true), red(true), red(), red(true)];
    }
    return colors;
  }

  handleRedirect(url: string) {
    if (url) {
      const isExternal = url.includes('http://') || url.includes('https://');
      if (isExternal) {
        if (this.isBrowserOnly) {
          window.location.href = url;
        }
      } else {
        this.router.navigateByUrl(url);
      }
    }
    this.router.navigateByUrl('/');
  }
}
